import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Microscope, Building2, Database, FlaskConical } from 'lucide-react';

const clientTypes = [
  'Drug development software',
  'Biosimulation and modeling tools',
  'Clinical trial technology',
  'Real-world data and analytics platforms',
  'Digital biomarkers and connected devices',
  'Regulatory and submission software',
  'Life sciences consulting services'
];

const IndustryCard = ({ id, index, icon: Icon, title, description, context, items }) => (
  <div id={id} className="bg-white border border-gray-200 p-8 hover:border-green transition-colors" data-testid={`industry-${id}`}>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-green/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-green" strokeWidth={1.5} />
          </div>
          <div>
            <span className="text-green text-sm font-medium">0{index}</span>
            <h2 className="font-heading text-xl text-navy">{title}</h2>
          </div>
        </div>
        <p className="text-gray-600 leading-relaxed mb-4">{description}</p>
        <p className="text-gray-500 text-sm leading-relaxed">{context}</p>
      </div>
      <div className="lg:col-span-7 lg:pl-8 lg:border-l border-gray-200">
        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Key Areas</h4>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
              <span className="w-1.5 h-1.5 bg-green rounded-full mt-2 flex-shrink-0"></span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

export const IndustriesPage = () => {
  return (
    <div data-testid="industries-page">
      {/* Hero Section */}
      <section className="hero-section py-16 md:py-20">
        <div className="container-law">
          <div className="breadcrumb text-white/60 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Industries</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">Industries</h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Built for companies operating at the intersection of technology, data, and life sciences.
          </p>
        </div>
      </section>

      {/* Client Types Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl text-navy mb-6">Who We Serve</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our clients build the software platforms, data assets, and specialized services that pharmaceutical and biotech companies depend on to develop, test, and commercialize new therapies. They operate globally, sit close to regulated data, and sell mission-critical solutions into highly complex environments.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Many combine SaaS offerings with professional services, data licensing, and long-term enterprise engagements.
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-8">
              <h3 className="font-heading text-xl text-navy mb-6">Our Clients Include</h3>
              <ul className="space-y-3">
                {clientTypes.map((type, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <span className="w-1.5 h-1.5 bg-green rounded-full mt-2 flex-shrink-0"></span>
                    {type}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industries List */}
      <section className="py-16">
        <div className="container-law">
          <div className="space-y-8">
            <IndustryCard
              id="life-sciences"
              index={1}
              icon={Microscope}
              title="Life Sciences & Pharmaceutical"
              description="Technology vendors serving pharmaceutical, biotech, and clinical research organizations."
              context="We represent technology companies selling into large pharmaceutical enterprises. We understand pharma procurement, regulatory requirements, and the complex compliance frameworks that shape these relationships."
              items={['Global deployments', 'Validation requirements', 'Inspection readiness', 'FDA/EMA frameworks', 'Long-term enterprise relationships']}
            />
            <IndustryCard
              id="healthcare-tech"
              index={2}
              icon={Building2}
              title="Healthcare Technology"
              description="Digital health platforms, healthcare analytics, and health IT vendors."
              context="Healthcare technology operates under unique regulatory and procurement constraints. We help healthtech vendors navigate these requirements while building scalable commercial relationships."
              items={['HIPAA compliance and BAAs', 'Healthcare interoperability', 'Health system procurement', 'Patient data privacy', 'Cross-border data transfers']}
            />
            <IndustryCard
              id="saas-data"
              index={3}
              icon={Database}
              title="SaaS & Data Platforms"
              description="Enterprise software and data platform companies serving life sciences."
              context="We work with SaaS companies at various stages—from early-stage vendors building first enterprise contracts to established platforms refining frameworks for global pharmaceutical customers."
              items={['Enterprise SaaS models', 'Data licensing and monetization', 'Platform terms', 'API agreements', 'Regulated data handling']}
            />
            <IndustryCard
              id="clinical-analytics"
              index={4}
              icon={FlaskConical}
              title="Clinical & Real-World Evidence"
              description="Companies working with clinical data, RWE, and healthcare analytics."
              context="Clinical and RWE data transactions involve complex regulatory, privacy, and intellectual property considerations. We help structure arrangements that enable commercial use while maintaining compliance."
              items={['RWE data arrangements', 'Clinical data licensing', 'Research collaborations', 'Publication provisions', 'Regulatory data requirements']}
            />
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-gray-50">
        <div className="container-law">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/practice-areas"
              className="bg-white border border-gray-200 p-8 hover:border-green transition-colors group"
              data-testid="related-practice-areas"
            >
              <p className="text-green text-sm font-medium mb-2">Related</p>
              <h3 className="font-heading text-xl text-navy mb-2 group-hover:text-green transition-colors">Practice Areas</h3>
              <p className="text-gray-600 text-sm mb-4">Explore our specific practice areas and agreements we handle.</p>
              <span className="text-green font-medium text-sm flex items-center gap-1">
                Learn more <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </span>
            </Link>
            <Link
              to="/contact"
              className="bg-navy p-8 group"
              data-testid="related-contact"
            >
              <p className="text-green text-sm font-medium mb-2">Get Started</p>
              <h3 className="font-heading text-xl text-white mb-2">Contact Us</h3>
              <p className="text-white/70 text-sm mb-4">Discuss your industry-specific commercial matters.</p>
              <span className="text-green font-medium text-sm flex items-center gap-1">
                Get in touch <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
